import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { DataGrid, GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import { RiExpandUpDownLine } from "react-icons/ri";
import {
  IconButton,
  LinearProgress,
  Button,
  CircularProgress,
} from "@mui/material";
import moment from "moment";
import useAuth from "../../context/AuthProvider";
import formatPrice from "../../helper/currencyFormatter";
import { StateReportModel } from "../../models/StateReportModel";
import { config } from "../../Constants";
import { AddEditStateReport } from "../AddEdit/AddEditStateReport";
import { Br18Form } from "../AddEdit/Br18Form";
import { br18Data_resp, br9Data_resp } from "../../models/StateReportModel";
import StateReportFileNowDialog from "../Elements/StateReportFileNowDialog";
import { apiClient } from "../../helper/api";
import Swal from "sweetalert2";
import { RemoveRedEyeOutlined } from "@mui/icons-material";
import { StateReportPreviewDialog } from "../Elements/StateReportPreviewDialog";

interface Iprops {
  itemList: StateReportModel[];
  totalCount: number;
  setFilterModel: any;
  setSortModel: any;
  selectedTab: number;
  setSelectedRows: React.Dispatch<
    React.SetStateAction<StateReportModel[] | undefined>
  >;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  _currentPage: number;
  _pageSize: number;
  selectedRows: StateReportModel[] | undefined;
  onRefresh?: () => void;
  states: { stateId: number; stateName: string }[];
  setSelectedTab?: React.Dispatch<React.SetStateAction<number>>;
}

export const columnWidth = {
  homeState: 70,
  reportName: 190,
  period: 140,
  dueDate: 110,
  isPayment: 130,
  isReport: 120,
  paymentType: 140,
  isWetSignature: 140,
  isNotaryStamp: 70,
  licensee: 240,
  licenseNumber: 170,
  client: 190,
  isMainLicensee: 60,
  isZeroIndividual: 60,
  isZeroEntity: 60,
  status: 60,
  isLargeReport: 60,
  notesToClient: 140,
  filerName: 90,
  taxAmount: 120,
  actions1: 80,
  dateFinalReportSentToClient: 190,
  fileNow: 100,
  edit: 80,
  preview: 80,
};

export const StateReportList: React.FC<Iprops> = ({
  itemList,
  totalCount,
  setFilterModel,
  setSortModel,
  selectedTab,
  setSelectedRows,
  setCurrentPage,
  setPageSize,
  _currentPage,
  _pageSize,
  selectedRows,
  onRefresh,
  states,
  setSelectedTab,
}) => {
  const { user } = useAuth();
  const token = user?.token;
  const [br18Data, setBr18Data] = useState<br18Data_resp>({
    e24GrossPremiumsCharged: "",
    e25AmountOfReturnedPremiums: "",
    a33Attestation: "",
    b33DateInFormat: "",
    d33SpecialBrokerName: "",
    b36BusinessAddress: "",
    b37TelephoneNumber: "",
    b38BrokerLicenseNumber: "",
    b39AuthorizedOfficer: "",
    b40EmailAddress: "",
    b41PreparersName: "",
    b42PreparersTelephone: "",
    b43PreparersEMailAddress: "",
  });
  const [br9Data, setBr9Data] = useState<Array<br9Data_resp>>([]);
  const [openBr18, setOpenBr18] = useState(false);
  const [loading] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [formData, setFormData] = useState<StateReportModel>();
  const [openFileNowDialog, setOpenFileNowDialog] = useState(false);
  const [selectedRowForFileNow, setSelectedRowForFileNow] =
    useState<StateReportModel | null>(null);
  const [loadingEditData, setLoadingEditData] = useState<number | null>(null); // Track which row is loading
  const [loadingPreviewDataId, setLoadingPreviewDataId] = useState<
    number | null
  >(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const handleEdit = async (rowData: StateReportModel) => {
    // If stateReportId exists, fetch data from backend
    if (rowData?.stateReportId && rowData.stateReportId > 0) {
      setLoadingEditData(rowData.stateReportId);
      try {
        const response = await apiClient("/StateReport/GetStateReportById", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: rowData.stateReportId,
          }),
        });

        if (response?.status === true && response?.data) {
          setFormData(response.data as StateReportModel);
          setOpenEdit(true);
        } else {
          throw new Error(
            response?.message || "Failed to fetch state report data",
          );
        }
      } catch (error: any) {
        console.error("Error fetching state report data:", error);
        Swal.fire({
          title: "Error",
          text:
            error?.message ||
            "Failed to fetch state report data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingEditData(null);
      }
    } else {
      // If no stateReportId, use row data directly (for new records)
      setFormData(rowData);
      setOpenEdit(true);
    }
  };

  // Handle URL parameters for opening edit dialog from email link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const stateReportIdParam = urlParams.get("stateReportId");
    const openEditParam = urlParams.get("openEdit");

    if (openEditParam === "true" && stateReportIdParam) {
      const stateReportId = parseInt(stateReportIdParam, 10);
      if (!isNaN(stateReportId) && stateReportId > 0) {
        // Set selectedTab to 2 (Completed Queue) by default when opening from URL
        if (setSelectedTab) {
          setSelectedTab(2);
        }
        // Reuse handleEdit function with a minimal object containing stateReportId
        handleEdit({ stateReportId } as StateReportModel).then(() => {
          // Clean up the URL parameters after handling
          const newUrl = window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, setSelectedTab]);

  const handleDocIcon = (rowData: StateReportModel) => {
    fetch(
      `${config.url.API_URL}/StateReports/ManualSubmission_MA?partnerId=${rowData.partnersId}`,
      {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
      },
    )
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((responseJson) => {
        const br9Temp = responseJson.data.bR9.map((item: br9Data_resp) => {
          item.specBkrLicense = (rowData as any).licenseNumber;
          if (item.grossPremium < 0) {
            item.returnPremium = -item.grossPremium;
            item.grossPremium = 0;
          }
          return item;
        });

        const br18Temp = {
          ...responseJson.data.bR18,
          b38BrokerLicenseNumber: (rowData as any).licenseNumber,
          e25AmountOfReturnedPremiums: br9Temp.reduce(
            (sum: number, obj: any) => sum + obj.returnPremium,
            0,
          ),
          e24GrossPremiumsCharged: br9Temp.reduce(
            (sum: number, obj: any) => sum + obj.grossPremium,
            0,
          ),
        };

        setBr18Data(br18Temp);
        setBr9Data(br9Temp);
        setOpenBr18(true);
      })
      .catch((error) => {
        console.log(error);
        setOpenBr18(false);
      });
  };

  const handleClose = () => {
    setOpenEdit(false);
    setFormData(undefined); // Clear form data to prevent carryover
  };

  const handleBrClose = () => {
    setOpenBr18(false);
  };

  const handleSubmit = () => {
    setOpenEdit(false);
    setFormData(undefined); // Clear form data to prevent carryover
    // Refresh the data after successful submission (only current queue)
    if (onRefresh) {
      onRefresh();
    }
  };

  const onRowsSelectionHandler = (ids: GridSelectionModel) => {
    const selectedIDs = new Set(ids);
    const selectedRowData = itemList.filter((row) =>
      selectedIDs.has(row?.rowNum ?? ""),
    );
    setSelectedRows(selectedRowData);
  };

  const createColumn = (
    field: string,
    headerName: string,
    minWidth: number,
    renderCell?: (params: any) => React.ReactNode,
  ): GridColDef => ({
    field,
    headerName,
    type: "string",
    headerAlign: "center",
    hideSortIcons: true,
    renderHeader: (params) => (
      <div>
        <span className="table-card-title">{params.colDef.headerName}</span>
        <RiExpandUpDownLine />
      </div>
    ),
    align: "center",
    minWidth,
    ...(renderCell && { renderCell }),
  });

  const createDateColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => (
      <div>
        {params.row[field] !== null && params.row[field] !== ""
          ? moment(params.row[field]).format("MM/DD/YYYY")
          : ""}
      </div>
    ));

  const createBooleanColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => {
      const value = params.value;
      if (value === null || value === undefined || value === "") {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              color: "#999",
              fontStyle: "italic",
            }}
          >
            N/A
          </Box>
        );
      }

      // Handle various boolean representations from API
      const isTrue =
        String(value).toLowerCase() === "true" || value === true || value === 1;

      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          {isTrue ? "Yes" : "No"}
        </Box>
      );
    });

  const createCurrencyColumn = (
    field: string,
    headerName: string,
    minWidth: number,
  ): GridColDef =>
    createColumn(field, headerName, minWidth, (params) => {
      const value = params.value;
      if (value === null || value === undefined || value === "") {
        return (
          <Box display="flex" alignItems="left" justifyContent="left">
            $0.00
          </Box>
        );
      }
      // Convert number to string for formatPrice function
      const stringValue =
        typeof value === "number" ? value.toString() : value || "0";
      return (
        <Box display="flex" alignItems="left" justifyContent="left">
          {formatPrice(stringValue)}
        </Box>
      );
    });

  const handleFileNow = async (rowData: StateReportModel) => {
    setSelectedRowForFileNow(rowData);
    setOpenFileNowDialog(true);
  };

  const handlePreview = async (rowData: StateReportModel) => {
    // If stateReportId exists, fetch data from backend
    if (rowData?.stateReportId && rowData.stateReportId > 0) {
      setLoadingPreviewDataId(rowData.stateReportId);
      try {
        const response = await apiClient("/StateReport/GetStateReportById", {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          }),
          body: JSON.stringify({
            stateReportId: rowData.stateReportId,
          }),
        });

        if (response?.status === true && response?.data) {
          setSelectedRowForFileNow(response.data as StateReportModel);
          setPreviewDialogOpen(true);
        } else {
          throw new Error(
            response?.message || "Failed to fetch state report data",
          );
        }
      } catch (error: any) {
        console.error("Error fetching state report data:", error);
        Swal.fire({
          title: "Error",
          text:
            error?.message ||
            "Failed to fetch state report data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingPreviewDataId(null);
      }
    } else {
      // If no stateReportId, use row data directly (for new records)
      setSelectedRowForFileNow(rowData);
      setPreviewDialogOpen(true);
    }
  };

  const handleFileNowDialogClose = () => {
    setOpenFileNowDialog(false);
    setSelectedRowForFileNow(null);
  };

  const handleFileNowDialogSubmit = (data: any) => {
    // Handle the File Now submission here
    console.log("File Now submitted:", data);
    handleFileNowDialogClose();
    setSelectedRows([]);
    if (onRefresh) {
      onRefresh();
    }
  };

  const createFileNowColumn = (): GridColDef => ({
    field: "fileNow",
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["fileNow"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Button
        variant="contained"
        onClick={() => handleFileNow(params.row)}
        sx={{
          backgroundColor: "#4CAF50",
          color: "white",
          "&:hover": {
            backgroundColor: "#45a049",
          },
          borderRadius: "4px",
          padding: "6px 16px",
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 500,
        }}
      >
        File Now
      </Button>
    ),
  });

  const createEditColumn = (): GridColDef => ({
    field: "edit",
    flex: 0.4,
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["edit"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      // Don't show edit icon if isDoneByClient is true
      const isDoneByClient =
        params.row.isDoneByClient === true ||
        String(params.row.isDoneByClient) === "true";
      if (isDoneByClient) {
        return null;
      }

      const isLoading = loadingEditData === params.row.stateReportId;
      return (
        <IconButton
          color="primary"
          onClick={() => handleEdit(params.row)}
          disabled={isLoading}
          sx={{ position: "relative" }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z"
                stroke="#02A4E3"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </IconButton>
      );
    },
  });

  const createPreviewColumn = (): GridColDef => ({
    field: "preview",
    flex: 0.4,
    headerName: "",
    hideSortIcons: true,
    minWidth: columnWidth["preview"],
    align: "center",
    headerAlign: "center",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const isLoading = loadingPreviewDataId === params.row.stateReportId;
      return (
        <IconButton
          color="primary"
          onClick={() => handlePreview(params.row)}
          disabled={isLoading}
          sx={{ position: "relative" }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            <RemoveRedEyeOutlined fontSize="large" sx={{ color: "gray" }} />
          )}
        </IconButton>
      );
    },
  });

  const createActionsColumn = (): GridColDef => ({
    field: "stateRule",
    flex: 0.4,
    headerName: "Edit",
    hideSortIcons: true,
    minWidth: columnWidth["actions1"],
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <div>
        <IconButton color="primary" onClick={() => handleEdit(params.row)}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z"
              stroke="#02A4E3"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
        {params.row.stateName === "MA" && (
          <IconButton color="primary" onClick={() => handleDocIcon(params.row)}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M9.5 0a.5.5 0 0 1 .5.5.5.5 0 0 0 .5.5.5.5 0 0 1 .5.5V2a.5.5 0 0 1-.5.5h-5A.5.5 0 0 1 5 2v-.5a.5.5 0 0 1 .5-.5.5.5 0 0 0 .5-.5.5.5 0 0 1 .5-.5z" />
              <path d="M3 2.5a.5.5 0 0 1 .5-.5H4a.5.5 0 0 0 0-1h-.5A1.5 1.5 0 0 0 2 2.5v12A1.5 1.5 0 0 0 3.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-12A1.5 1.5 0 0 0 12.5 1H12a.5.5 0 0 0 0 1h.5a.5.5 0 0 1 .5.5v12a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5z" />
              <path d="M10.854 7.854a.5.5 0 0 0-.708-.708L7.5 9.793 6.354 8.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z" />
            </svg>
          </IconButton>
        )}
      </div>
    ),
  });

  const getColumns = (): GridColDef[] => {
    const baseColumns = [
      createColumn("stateName", "State", columnWidth["homeState"]),
      createColumn("name", "Report Name", columnWidth["reportName"]),
      createColumn("period", "Period", columnWidth["period"]),
      createDateColumn("dueDate", "Due Date", columnWidth["dueDate"]),
      createBooleanColumn(
        "payment",
        "Payment Required",
        columnWidth["isPayment"],
      ),
      createBooleanColumn("report", "Report Required", columnWidth["isReport"]),
      createColumn("paymentType", "Payment Type", columnWidth["paymentType"]),
      createBooleanColumn(
        "wetSignature",
        "Wet Signature Required",
        columnWidth["isWetSignature"],
      ),
      createBooleanColumn(
        "notaryStamp",
        "Notary Stamp Required",
        columnWidth["isNotaryStamp"],
      ),
      createColumn("licensee", "Licensee", columnWidth["licensee"]),
      createColumn(
        "licenseNumber",
        "License Number",
        columnWidth["licenseNumber"],
      ),
      createColumn("client", "Client", columnWidth["client"]),
      createBooleanColumn(
        "mainLicensee",
        "Main Licensee",
        columnWidth["isMainLicensee"],
      ),
      createBooleanColumn(
        "zeroIndividual",
        "Zero Individual",
        columnWidth["isZeroIndividual"],
      ),
      createBooleanColumn(
        "zeroEntity",
        "Zero Entity",
        columnWidth["isZeroEntity"],
      ),
      createColumn("status", "Status", columnWidth["status"]),
      createBooleanColumn(
        "largeReport",
        "Large Report",
        columnWidth["isLargeReport"],
      ),
      createColumn("notes", "Notes To Client", columnWidth["notesToClient"]),
      createColumn("filerName", "Filer Name", columnWidth["filerName"]),
      createCurrencyColumn(
        "taxAmount",
        "Tax Reported",
        columnWidth["taxAmount"],
      ),
    ];

    if (selectedTab === 1) {
      // Outstanding tab: Show File Now button at the beginning
      return [createFileNowColumn(), ...baseColumns, createActionsColumn()];
    } else if (selectedTab === 2) {
      // Completed tab: Show Edit icon at the beginning instead of File Now
      return [
        createEditColumn(),
        createPreviewColumn(),
        ...baseColumns.slice(0, -1), // Remove actions column
        createDateColumn(
          "dateFinalReportSentToClient",
          "Date Completed",
          columnWidth["dateFinalReportSentToClient"],
        ),
        createCurrencyColumn(
          "taxAmount",
          "Tax Reported",
          columnWidth["taxAmount"],
        ),
      ];
    }

    return [];
  };

  const EditToolbar = () => (
    <React.Fragment>
      <div
        style={{
          position: "fixed",
          top: "0",
          left: "0",
          zIndex: "9990",
          width: "100%",
        }}
      >
        {loading && (
          <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />
        )}
      </div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div style={{ height: "100%" }}>
        <DataGrid
          getRowId={(row) => row?.rowNum}
          rows={itemList}
          columns={getColumns()}
          pageSize={_pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          page={_currentPage - 1}
          onPageChange={(newPage) => setCurrentPage(newPage + 1)}
          pagination
          checkboxSelection={true}
          selectionModel={
            selectedRows
              ?.map((item) => item.rowNum)
              .filter(
                (rowNum): rowNum is Exclude<typeof rowNum, undefined> =>
                  rowNum !== undefined,
              ) || []
          }
          onSelectionModelChange={(newRowSelectionModel) => {
            onRowsSelectionHandler(newRowSelectionModel);
          }}
          rowCount={totalCount || 0}
          rowsPerPageOptions={[25, 50, 100]}
          paginationMode="server"
          sortingMode="server"
          sortingOrder={["desc", "asc"]}
          onFilterModelChange={setFilterModel}
          onSortModelChange={setSortModel}
          style={{ textAlign: "center" }}
          components={{
            Toolbar: EditToolbar,
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "normal",
            },
            fontFamily: "'Proxima Nova', sans-serif;",
            border: "0px",
            fontSize: "16px",
          }}
          experimentalFeatures={{ newEditingApi: true }}
          columnVisibilityModel={{
            paymentDate: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
            paymentAmount: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
            lineOfBusiness: user?.userRoles.split(",").includes("Super Admin")
              ? true
              : false,
          }}
        />
        <AddEditStateReport
          handleClose={handleClose}
          Open={openEdit}
          StateRuleMatrixCL={formData}
          handleSubmit={handleSubmit}
          isStateReport={true}
          selectedTab={selectedTab}
        />
        <Br18Form
          handleClose={handleBrClose}
          Open={openBr18}
          br18Data={br18Data}
          br9Data={br9Data}
        />
        <StateReportFileNowDialog
          open={openFileNowDialog}
          handleClose={handleFileNowDialogClose}
          handleSubmit={handleFileNowDialogSubmit}
          selectedRow={selectedRowForFileNow}
          states={states}
        />
        {/* State Report Preview Dialog */}
        <StateReportPreviewDialog
          open={previewDialogOpen}
          onClose={() => {
            setPreviewDialogOpen(false);
          }}
          stateReport={selectedRowForFileNow}
          selectedTab={selectedTab}
          states={states}
        />
      </div>
    </React.Fragment>
  );
};
